<template>
  <!-- Ficha KPI del proyecto: indicadores clave (avance, márgenes, importes, riesgo) + estado general. -->
  <div class="kpi-card">
    <!-- Cabecera: código (badge azul) → nombre del proyecto → cliente → estado general (todo a la izquierda). -->
    <div class="kpi-head">
      <span class="h-code">{{ code }}</span>
      <span class="h-name">{{ name }}</span>
      <span v-if="client" class="h-client">{{ client }}</span>
      <span class="h-state">
        <span class="h-state-label">{{ $t('prm_dash_general_state') }}</span>
        <sem-dot :state="rag" :title="$t('prm_dash_rag_' + rag)"></sem-dot>
      </span>
    </div>

    <table class="ficha">
      <thead>
        <tr>
          <th class="ic-col"></th>
          <th>{{ $t('prm_dash_col_indicator') }}</th>
          <th class="r">{{ $t('prm_dash_col_value') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.key">
          <td class="ic-col">
            <span class="ic" :style="{ background: iconColor(row) + '22', color: iconColor(row) }">
              <b-icon pack="fas" :icon="row.icon" size="is-small"></b-icon>
            </span>
          </td>
          <td class="lbl-col">
            <span class="lbl">{{ $t(row.label) }}</span><span v-if="row.sub" class="sub"> · {{ row.sub }}</span>
            <source-chip v-if="mode" :source="row.source"></source-chip>
          </td>
          <td class="val-col"><b :style="{ color: row.accent || '#1e3046' }">{{ row.value }}</b></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
  import SourceChip from '../summary/source-chip.vue'
  import SemDot from './sem-dot.vue'
  import { fmtEur, fmtPct, fmtDate, fmtEurSigned, fmtPctSigned } from '../shared/format'
  import { ACCENT, progressColor, marginColor } from './dashboard-model'

  // Acento de texto según semáforo (juego oscuro del diseño, distinto al del punto).
  const ACCENT_BY_STATE = { green: ACCENT.good, amber: ACCENT.warn, red: ACCENT.bad }

  export default {
    name: 'PrmDashKpiFicha',

    components: { SourceChip, SemDot },

    props: {
      sources: { type: Object, default: () => ({}) },
      doc: { type: Object, default: () => ({}) },
      dash: { type: Object, default: () => ({}) },
      rag: { type: String, default: 'green' },
      penaltyTotal: { type: Number, default: 0 },
      prmComputed: { type: Object, default: null }, // { workPct, coConfirmed, coExpected } del PRM
      mode: { type: Boolean, default: false },
    },

    computed: {
      code () { return this.sources.sapCode || '—' },
      name () { return this.sources.projectName || '—' },
      client () { return (this.doc.s01 || {}).client || '' },
      prm () { return this.sources.prm || {} },
      progress () { return this.sources.progress || {} },

      rows () {
        const prm = this.prm
        const prog = this.progress
        const hasPrm = this.sources.prm != null
        const pc = this.prmComputed
        const real = Number(prog.real) || 0
        const current = Number(prog.current) || 0
        const scheduled = Number(prog.scheduled) || 0
        const total = Number(prm.totalAmount) || 0
        // Desviación de margen = margen actual (€) − margen comercial (€).
        //   margen actual €    = margen actual %    × importe total
        //   margen comercial € = margen comercial % × importe contractual
        const marginDev = hasPrm ? (Number(prm.actualMargin) * total - Number(prm.commercialMargin) * Number(prm.contractAmount)) : null
        const marginDevPct = hasPrm ? (Number(prm.actualMargin) - Number(prm.commercialMargin)) * 100 : null
        // PoC = coste incurrido / coste total estimado (del PRM). Backlog = importe total × (1 − avance real).
        const poc = pc && pc.workPct != null ? Number(pc.workPct) : null
        const backlog = hasPrm ? total * (1 - real / 100) : null

        return [
          // Jefe de proyecto: fuente única = cabecera de la Plantilla PRM (solo lectura aquí).
          { key: 'pm', icon: 'user', label: 'prm_dash_kpi_pm', source: 'prm', value: this.sources.projectManager || '-', sub: this.deliverySub },
          { key: 'progress', icon: 'chart-line', label: 'prm_dash_kpi_progress', source: 'calc',
            value: `${this.n(current)} % / ${this.n(scheduled)} %`, accent: ACCENT_BY_STATE[progressColor(current, scheduled)] },
          { key: 'margin', icon: 'percent', label: 'prm_dash_kpi_margin', source: 'prm',
            value: hasPrm ? `${fmtPct(prm.commercialMargin)} / ${fmtPct(prm.actualMargin)}` : '-',
            accent: hasPrm ? ACCENT_BY_STATE[marginColor(prm.commercialMargin, prm.actualMargin)] : '' },
          { key: 'marginDev', icon: 'arrow-trend-up', label: 'prm_dash_kpi_margin_dev', source: 'calc',
            value: marginDev == null ? '-' : fmtEurSigned(marginDev), sub: marginDevPct != null ? fmtPctSigned(marginDevPct) : '',
            accent: marginDev != null ? (marginDev < 0 ? ACCENT.bad : ACCENT.good) : '' },
          { key: 'sale', icon: 'sack-dollar', label: 'prm_dash_kpi_total_sale', source: 'prm', value: this.money(prm.totalAmount) },
          { key: 'backlog', icon: 'boxes-stacked', label: 'prm_dash_kpi_backlog', source: 'calc', value: this.money(backlog) },
          { key: 'coApproved', icon: 'circle-check', label: 'prm_dash_kpi_co_approved', source: 'prm', value: pc ? fmtEur(pc.coConfirmed) : '-' },
          { key: 'coNegotiation', icon: 'hourglass-half', label: 'prm_dash_kpi_co_negotiation', source: 'prm',
            value: pc ? fmtEur(pc.coExpected) : '-', sub: this.$t('prm_dash_kpi_co_pending'),
            accent: pc && pc.coExpected > 0 ? ACCENT.warn : '' },
          { key: 'penalties', icon: 'gavel', label: 'prm_dash_kpi_penalties', source: 'contract',
            value: fmtEur(this.penaltyTotal), sub: this.$t('prm_dash_kpi_contractual'),
            accent: this.penaltyTotal > 0 ? ACCENT.bad : ACCENT.good },
          { key: 'poc', icon: 'chart-pie', label: 'prm_dash_kpi_poc', source: 'prm', value: poc != null ? fmtPct(poc) : '-' },
          { key: 'incidents', icon: 'triangle-exclamation', label: 'prm_dash_kpi_open_incidents', source: 'incid',
            value: String(this.openIncidents), accent: this.openIncidents > 0 ? ACCENT.bad : ACCENT.good },
          { key: 'penalizing', icon: 'flag', label: 'prm_dash_kpi_penalizing', source: 'contract',
            value: String(this.penalizing), accent: this.penalizing > 0 ? ACCENT.bad : ACCENT.good },
        ]
      },

      openIncidents () { return (this.sources.incidents || []).filter((r) => r.status !== 'closed').length },
      penalizing () { return ((this.doc.s03 || {}).penaltyMilestones || []).filter((r) => r.penalizing === 'yes').length },

      // Subtítulo del PM: fecha de entrega (fin actual del proyecto, del Administrador).
      deliverySub () {
        const end = (this.sources.dates || {}).currentEnd
        return end ? `${this.$t('prm_dash_delivery')} ${fmtDate(end)}` : ''
      },
    },

    methods: {
      n (v) { return (Number(v) || 0).toFixed(1) },
      money (v) { return v != null ? fmtEur(v) : '-' },
      // Color del icono: el acento de la fila, o azul neutro si no tiene.
      iconColor (row) { return row.accent || '#3a6bc8' },
    },
  }
</script>

<style lang="scss" scoped>
  .kpi-card { background: #fff; border: 1px solid #d4dae3; border-radius: 6px; overflow: hidden; }

  // Cabecera compuesta: código (badge azul), nombre, cliente y estado general, todo a la izquierda.
  .kpi-head {
    display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
    background: #e8eef5; border-bottom: 1px solid #c9d5e3; box-shadow: inset 3px 0 0 0 #3a6bc8;
    padding: 6px 10px;
  }
  .h-code {
    display: inline-block; background: #2b5cad; color: #fff; font-size: 10px; font-weight: 700;
    letter-spacing: .03em; padding: 2px 8px; border-radius: 4px;
  }
  .h-name { font-size: 12px; font-weight: 700; color: #1e3046; text-transform: uppercase; letter-spacing: .02em; }
  .h-client { font-size: 11px; color: #6b7787; }
  .h-state { display: inline-flex; align-items: center; gap: 6px; }
  .h-state-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: #6b7787; }

  .ficha { width: 100%; border-collapse: collapse; }
  .ficha th {
    background: #3d6b9a; color: #fff; font-size: 9px; text-transform: uppercase; letter-spacing: .03em;
    font-weight: 700; padding: 5px 10px; text-align: left; white-space: nowrap;
  }
  .ficha th.r { text-align: right; }
  .ficha td { border-bottom: 1px solid #eef0f3; padding: 5px 10px; vertical-align: middle; }
  // Cebra + hover (un pelín más oscuro), como el resto de tablas.
  .ficha tbody tr:nth-child(even) td { background: #f9fafb; }
  .ficha tbody tr:hover td { background: #eef2f7; }

  .ic-col { width: 34px; }
  .ic {
    display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px;
    border-radius: 6px; font-size: 11px;
  }
  .lbl-col { width: 55%; }
  .lbl { font-size: 11px; color: #1e3046; font-weight: 600; }
  .sub { font-size: 10px; color: #9aa4b2; }
  .val-col { text-align: right; white-space: nowrap; }
  .val-col b { font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums; }
</style>
